export interface IconPaletteColor {
  name: string;
  value: string;
  border?: boolean;
}

export const DMSUITE_COLORS = {
  blue: '#306EE3',
  cyan: '#25C5D9',
  indigo: '#352C70',
  gold: '#FFB846',
  green: '#9EDB62',
  white: '#FFFFFF',
} as const;

export const dmSuiteIconPalette: IconPaletteColor[] = [
  {
    name: 'Royal Blue',
    value: '#306EE3',
  },
  {
    name: 'Cyan',
    value: '#25C5D9',
  },
  {
    name: 'Indigo',
    value: '#352C70',
  },
  {
    name: 'Gold',
    value: '#FFB846',
  },
  {
    name: 'Green',
    value: '#9EDB62',
  },
  {
    name: 'White',
    value: '#FFFFFF',
    border: true,
  },
];

export const DEFAULT_ICON_COLOR = '#306EE3';
export const DEFAULT_ICON_SIZE = 22;
export const FALLBACK_ICON_NAME = 'mdi:application-outline';

export interface IconGradientConfig {
  preset?: string | null;
  type: 'linear' | 'radial';
  angle?: number;
  colors: string[];
}

export interface GradientPreset {
  id: string;
  name: string;
  type: 'linear' | 'radial';
  angle: number;
  colors: string[];
  description?: string;
}

export const DMSUITE_GRADIENTS: GradientPreset[] = [
  {
    id: 'blue-cyan',
    name: 'Blue Cyan',
    type: 'linear',
    angle: 135,
    colors: ['#306EE3', '#25C5D9'],
    description: 'Primary DMSuite tech gradient',
  },
  {
    id: 'indigo-blue',
    name: 'Indigo Blue',
    type: 'linear',
    angle: 135,
    colors: ['#352C70', '#306EE3'],
    description: 'Deep contrast gradient',
  },
  {
    id: 'cyan-blue-indigo',
    name: 'Cyan Blue Indigo',
    type: 'linear',
    angle: 135,
    colors: ['#25C5D9', '#306EE3', '#352C70'],
    description: 'Dynamic 3-color flow',
  },
  {
    id: 'dm-brand-spectrum',
    name: 'DMSuite Spectrum',
    type: 'linear',
    angle: 135,
    colors: ['#FFB846', '#9EDB62', '#25C5D9', '#306EE3'],
    description: 'Official brand spectrum logo gradient',
  },
  {
    id: 'blue-indigo',
    name: 'Blue Indigo',
    type: 'linear',
    angle: 90,
    colors: ['#306EE3', '#352C70'],
    description: 'Vertical corporate flow',
  },
  {
    id: 'blue-glow',
    name: 'Blue Glow',
    type: 'radial',
    angle: 0,
    colors: ['#25C5D9', '#306EE3'],
    description: 'Cyan center with Royal Blue glow',
  },
  {
    id: 'indigo-glow',
    name: 'Indigo Glow',
    type: 'radial',
    angle: 0,
    colors: ['#306EE3', '#352C70'],
    description: 'Royal Blue center with Deep Indigo glow',
  },
  {
    id: 'spectrum-glow',
    name: 'Spectrum Glow',
    type: 'radial',
    angle: 0,
    colors: ['#FFB846', '#9EDB62', '#306EE3'],
    description: 'Vibrant radial tri-color burst',
  },
];

export interface GradientDirectionOption {
  label: string;
  angle: number;
  icon: string;
  symbol: string;
}

export const GRADIENT_DIRECTION_OPTIONS: GradientDirectionOption[] = [
  { label: 'Horizontal', angle: 0, icon: 'fas fa-arrow-right', symbol: '→' },
  { label: 'Vertical', angle: 90, icon: 'fas fa-arrow-down', symbol: '↓' },
  { label: 'Diagonal Down', angle: 135, icon: 'fas fa-arrow-down-right', symbol: '↘' },
  { label: 'Diagonal Up', angle: 45, icon: 'fas fa-arrow-up-right', symbol: '↗' },
];

export const DEFAULT_GRADIENT_CONFIG: IconGradientConfig = {
  preset: 'blue-cyan',
  type: 'linear',
  angle: 135,
  colors: ['#306EE3', '#25C5D9'],
};

export interface IconCollectionConfig {
  label: string;
  value: string | null;
  prefix?: string;
  description?: string;
}

export const ICONIFY_COLLECTIONS: IconCollectionConfig[] = [
  {
    label: 'All',
    value: null,
    description: 'All collections',
  },
  {
    label: 'Lucide',
    value: 'lucide',
    prefix: 'lucide',
    description: 'Clean, modern line icons',
  },
  {
    label: 'Tabler',
    value: 'tabler',
    prefix: 'tabler',
    description: 'Consistent stroke icons',
  },
  {
    label: 'Material',
    value: 'mdi',
    prefix: 'mdi',
    description: 'Material Design Icons',
  },
  {
    label: 'Phosphor',
    value: 'ph',
    prefix: 'ph',
    description: 'Flexible outline icons',
  },
  {
    label: 'Heroicons',
    value: 'heroicons',
    prefix: 'heroicons',
    description: 'Tailwind UI compatible icons',
  },
];

export const ICON_SIZE_OPTIONS: number[] = [16, 18, 20, 22, 24, 28, 32];

/**
 * Curated popular icons for DMSuite SaaS apps when browsing without a query.
 */
export const POPULAR_STARTER_ICONS: string[] = [
  // Dashboard & Analytics
  'lucide:layout-dashboard',
  'tabler:dashboard',
  'mdi:view-dashboard-outline',
  'ph:chart-bar',
  'lucide:bar-chart-3',
  'tabler:chart-pie',
  'lucide:trending-up',
  'ph:presentation-chart',

  // Communication & Messaging
  'lucide:inbox',
  'tabler:inbox',
  'mdi:inbox-outline',
  'ph:tray',
  'heroicons:inbox',
  'lucide:mail',
  'tabler:mail',
  'ph:envelope-simple',
  'lucide:message-square',
  'tabler:message',
  'ph:chat-circle-dots',
  'lucide:bell',
  'tabler:bell',
  'ph:bell-simple',

  // Management & CRM
  'lucide:users',
  'tabler:users',
  'mdi:account-group-outline',
  'ph:users-three',
  'lucide:user-check',
  'tabler:user-star',
  'ph:user-circle',
  'lucide:briefcase',
  'tabler:briefcase',
  'ph:suitcase-simple',
  'lucide:shield-check',
  'tabler:shield-lock',

  // Tools & Operations
  'lucide:settings',
  'tabler:settings',
  'mdi:cog-outline',
  'ph:gear',
  'lucide:sliders-horizontal',
  'lucide:calendar',
  'tabler:calendar',
  'ph:calendar',
  'lucide:check-square',
  'tabler:checkbox',
  'ph:check-square-offset',
  'lucide:clipboard-list',
  'tabler:clipboard-check',

  // Files & Content
  'lucide:folder',
  'tabler:folder',
  'ph:folder-simple',
  'lucide:file-text',
  'tabler:file-text',
  'ph:file-doc',
  'lucide:layers',
  'tabler:stack-2',
  'lucide:sparkles',
  'tabler:wand',
  'ph:sparkle',
  'lucide:globe',
  'tabler:world',
  'ph:globe-simple',
  'lucide:database',
  'tabler:database',
  'ph:database',
  'lucide:cloud',
  'tabler:cloud',
  'ph:cloud',
];
